<script setup>
import { computed, onMounted, ref, nextTick, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { debounce } from '@chatwoot/utils';
import AssistantAgentsAPI from 'dashboard/api/assistant/agents';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import BulkSelectBar from 'dashboard/components-next/assistant/shared/BulkSelectBar.vue';
import DeleteDialog from 'dashboard/components-next/assistant/pageComponents/DeleteDialog.vue';
import BulkDeleteDialog from 'dashboard/components-next/assistant/pageComponents/BulkDeleteDialog.vue';
import PageLayout from 'dashboard/components-next/assistant/PageLayout.vue';
import ResponseCard from 'dashboard/components-next/assistant/shared/ResponseCard.vue';
import CreateResponseDialog from 'dashboard/components-next/assistant/pageComponents/response/CreateResponseDialog.vue';
import ResponsePageEmptyState from 'dashboard/components-next/assistant/shared/emptyStates/ResponsePageEmptyState.vue';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('assistantResponses/getUIFlags');
const responseMeta = useMapGetter('assistantResponses/getMeta');
const responses = useMapGetter('assistantResponses/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);

const selectedResponse = ref(null);
const deleteDialog = ref(null);
const bulkDeleteDialog = ref(null);

const dialogType = ref('');
const searchQuery = ref('');

const createDialog = ref(null);

const pendingCount = useMapGetter('assistantResponses/getPendingCount');

// Auto-FAQ (У7): on-demand generation — mines resolved conversations into
// pending responses for review. Admin-only, gated by the auto_faq flag; no
// cron by design (LLM budget stays под контролем владельца).
const { isAdmin } = useAdmin();
const { accountId } = useAccount();
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const autoFaqEnabled = computed(
  () =>
    isAdmin.value &&
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.AUTO_FAQ)
);
const agents = useMapGetter('assistantAgents/getRecords');
const faqAgentId = ref(null);
const isGeneratingFaq = ref(false);

watch(
  agents,
  list => {
    if (!faqAgentId.value && list?.length) faqAgentId.value = list[0].id;
  },
  { immediate: true }
);

const generateFaq = async () => {
  if (!faqAgentId.value || isGeneratingFaq.value) return;
  isGeneratingFaq.value = true;
  try {
    const { data } = await AssistantAgentsAPI.generateFaq(faqAgentId.value);
    const count = (data.candidates || []).length;
    useAlert(t('ASSISTANT.RESPONSES.GENERATE_FAQ.SUCCESS', { count }));
    store.dispatch('assistantResponses/fetchPendingCount');
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('ASSISTANT.RESPONSES.GENERATE_FAQ.ERROR')
    );
  } finally {
    isGeneratingFaq.value = false;
  }
};

const handleDelete = () => {
  deleteDialog.value.dialogRef.open();
};

const handleCreate = () => {
  dialogType.value = 'create';
  nextTick(() => createDialog.value.dialogRef.open());
};

const handleEdit = () => {
  dialogType.value = 'edit';
  nextTick(() => createDialog.value.dialogRef.open());
};

const handleAction = ({ action, id }) => {
  selectedResponse.value = responses.value.find(response => id === response.id);
  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    }
    if (action === 'edit') {
      handleEdit();
    }
  });
};

const handleCreateClose = () => {
  dialogType.value = '';
  selectedResponse.value = null;
};

const updateURLWithFilters = (page, search) => {
  const query = {
    page: page || 1,
  };

  if (search) {
    query.search = search;
  }

  router.replace({ query });
};

const fetchResponses = (page = 1) => {
  const filterParams = { page, status: 'approved' };

  if (searchQuery.value) {
    filterParams.searchKey = searchQuery.value;
  }

  updateURLWithFilters(page, searchQuery.value);

  store.dispatch('assistantResponses/get', filterParams);
};

// Bulk action
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

const buildSelectedCountLabel = computed(() => {
  const count = responses.value?.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('ASSISTANT.RESPONSES.UNSELECT_ALL', { count })
    : t('ASSISTANT.RESPONSES.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('ASSISTANT.RESPONSES.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleCardHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const handleCardSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const fetchResponseAfterBulkAction = () => {
  const hasNoResponsesLeft = responses.value?.length === 0;
  const currentPage = responseMeta.value?.page;

  if (hasNoResponsesLeft) {
    const pageToFetch = currentPage > 1 ? currentPage - 1 : currentPage;
    fetchResponses(pageToFetch);
  } else {
    fetchResponses(currentPage);
  }

  bulkSelectedIds.value = new Set();
};

const onPageChange = page => {
  const hadSelection = bulkSelectedIds.value.size > 0;

  fetchResponses(page);

  if (hadSelection) {
    bulkSelectedIds.value = new Set();
  }
};

const onDeleteSuccess = () => {
  if (responses.value?.length === 0 && responseMeta.value?.page > 1) {
    onPageChange(responseMeta.value.page - 1);
  }
};

const onBulkDeleteSuccess = () => {
  fetchResponseAfterBulkAction();
};

const debouncedSearch = debounce(async () => {
  fetchResponses(1);
}, 500);

const initializeFromURL = () => {
  if (route.query.search) {
    searchQuery.value = route.query.search;
  }
  const pageFromURL = parseInt(route.query.page, 10) || 1;
  fetchResponses(pageFromURL);
};

const navigateToPendingFAQs = () => {
  router.push({ name: 'assistant_responses_pending' });
};

onMounted(() => {
  initializeFromURL();
  store.dispatch('assistantResponses/fetchPendingCount');
  store.dispatch('assistantAgents/get');
});
</script>

<template>
  <PageLayout
    :total-count="responseMeta.totalCount"
    :current-page="responseMeta.page"
    :button-policy="['administrator']"
    :header-title="$t('ASSISTANT.RESPONSES.HEADER')"
    :button-label="$t('ASSISTANT.RESPONSES.ADD_NEW')"
    :is-fetching="isFetching"
    :is-empty="!responses.length"
    :show-pagination-footer="!isFetching && !!responses.length"
    :feature-flag="FEATURE_FLAGS.AI_ASSISTANT"
    @update:current-page="onPageChange"
    @click="handleCreate"
  >
    <template #search>
      <div
        v-if="bulkSelectedIds.size === 0"
        class="flex gap-3 justify-between w-full items-center"
      >
        <Input
          v-model="searchQuery"
          :placeholder="$t('ASSISTANT.RESPONSES.SEARCH_PLACEHOLDER')"
          class="w-64"
          size="sm"
          type="search"
          autofocus
          @input="debouncedSearch"
        />
        <div v-if="autoFaqEnabled" class="flex items-center gap-2">
          <select
            v-if="agents.length > 1"
            v-model="faqAgentId"
            class="px-2 py-1.5 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 max-w-48"
          >
            <option v-for="a in agents" :key="a.id" :value="a.id">
              {{ a.name }}
            </option>
          </select>
          <Button
            variant="faded"
            color="slate"
            size="sm"
            icon="i-lucide-sparkles"
            :label="$t('ASSISTANT.RESPONSES.GENERATE_FAQ.BUTTON')"
            :is-loading="isGeneratingFaq"
            :disabled="isGeneratingFaq || !faqAgentId"
            @click="generateFaq"
          />
        </div>
      </div>
    </template>

    <template #subHeader>
      <BulkSelectBar
        v-model="bulkSelectedIds"
        :all-items="responses"
        :select-all-label="buildSelectedCountLabel"
        :selected-count-label="selectedCountLabel"
        :delete-label="$t('ASSISTANT.RESPONSES.BULK_DELETE_BUTTON')"
        class="w-fit"
        :class="{
          'mb-2': bulkSelectedIds.size > 0,
        }"
        @bulk-delete="bulkDeleteDialog.dialogRef.open()"
      />
    </template>

    <template #emptyState>
      <ResponsePageEmptyState @click="handleCreate" />
    </template>

    <template #body>
      <Banner
        v-if="pendingCount > 0"
        color="blue"
        class="mb-4 -mt-3"
        :action-label="$t('ASSISTANT.RESPONSES.PENDING_BANNER.ACTION')"
        @action="navigateToPendingFAQs"
      >
        {{ $t('ASSISTANT.RESPONSES.PENDING_BANNER.TITLE') }}
      </Banner>

      <div class="flex flex-col gap-4">
        <ResponseCard
          v-for="response in responses"
          :id="response.id"
          :key="response.id"
          :question="response.question"
          :answer="response.answer"
          :documentable="response.documentable"
          :status="response.status"
          :created-at="response.created_at"
          :updated-at="response.updated_at"
          :is-selected="bulkSelectedIds.has(response.id)"
          :selectable="hoveredCard === response.id || bulkSelectedIds.size > 0"
          :show-menu="!bulkSelectedIds.has(response.id)"
          :show-actions="false"
          @action="handleAction"
          @select="handleCardSelect"
          @hover="isHovered => handleCardHover(isHovered, response.id)"
        />
      </div>
    </template>

    <DeleteDialog
      v-if="selectedResponse"
      ref="deleteDialog"
      :entity="selectedResponse"
      type="Responses"
      @delete-success="onDeleteSuccess"
    />

    <BulkDeleteDialog
      v-if="bulkSelectedIds"
      ref="bulkDeleteDialog"
      :bulk-ids="bulkSelectedIds"
      type="response"
      @delete-success="onBulkDeleteSuccess"
    />

    <CreateResponseDialog
      v-if="dialogType"
      ref="createDialog"
      :type="dialogType"
      :selected-response="selectedResponse"
      @close="handleCreateClose"
    />
  </PageLayout>
</template>
